import styles from "./App.module.css";
import React, { useEffect, useState, createContext, useContext } from "react";
import { ReactNodeLike } from "prop-types";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";
import { IconDiamond } from "@wwg/icons";
import { AppFrame } from "@wwg/ui";

import mobileBridge from "./mobileBridge";
import * as Utils from "././utils";

import LOSTreeViewPage from "./pages/LOSTreeViewPage";
import LOSBranchViewPage from "./pages/LOSBranchViewPage";
import MyInfo from "./pages/MyInfo";
import EagleTracker from "./pages/EagleTracker";
import { EagleTrackerProfile } from "./pages/EagleTrackerProfile/EagleTrackerProfile";
import { Reports } from "./pages/Reports/Reports";
import { LOSPage } from "./pages/LOSPage/LOSPage";
import { LOSSearchPage } from "./pages/LOSSearchPage/LOSSearchPage";
import Settings from "./pages/Settings";

import { ReactComponent as MyInfoIcon } from "./components/icons/MyInfoIcon.svg";
import { ReactComponent as LOSIcon } from "./components/icons/LOSIcon.svg";
import { ReactComponent as EagleTrackerIcon } from "./components/icons/EagleTrackerIcon.svg";
import { ReactComponent as ReportsIcon } from "./components/icons/ReportsIcon.svg";
import { ReactComponent as SettingsIcon } from "./components/icons/SettingsIcon.svg";

import { useIBOGet } from "./useIBOGet";
import { useTranslation } from "react-i18next";

const AppDataContext = createContext(null);
export const useAppDataContext = () => useContext(AppDataContext);

export function App() {
  const {t} = useTranslation();
  const location = useLocation();
  const [initialLoad, setInitialLoad] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const { data: iboNumber, isLoading } = useIBOGet();

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const currentPath = location.pathname.replace(/\/$/, "");
    const navLinks = document.querySelectorAll<HTMLAnchorElement>("nav a");
    const isMobile = windowWidth <= 700;

    navLinks.forEach((link) => {
      const href = link.getAttribute("href")?.replace(/\/+$/, "");
      let isActive = false;

      if (currentPath === Utils.PATHS.LOS_DETAILS_INFO) {
        // On the LOS details info page we want My Info to be active instead of LOS
        isActive = href === Utils.PATHS.MY_INFO;
      } else {
        // Default behavior: exact match or LOS group routes
        isActive = href === currentPath || (href === Utils.PATHS.LOS && currentPath.startsWith(Utils.PATHS.LOS));
      }
      if (isActive) {
        if (isMobile) {
          link.style.backgroundColor = "#adb7c0";
          link.style.color = "#22252e";
        } else {
          link.style.backgroundColor = "#e9f0f1";
          link.style.color = "#3f7a81";
        }
      } else {
        link.style.color = "";
        link.style.backgroundColor = "";
      }
    });
  }, [location.pathname, windowWidth]);

  // Add a body class for specific routes so we can override AppFrame's built-in
  // active styles when needed (e.g. make My Info active for /los-details-info)
  useEffect(() => {
    const currentPath = location.pathname.replace(/\/$/, "");
    const bodyClass = "route-los-details-info";
    if (currentPath === Utils.PATHS.LOS_DETAILS_INFO) {
      document.body.classList.add(bodyClass);
    } else {
      document.body.classList.remove(bodyClass);
    }
    return () => document.body.classList.remove(bodyClass);
  }, [location.pathname]);

  useEffect(() => {
    if (initialLoad) {
      setInitialLoad(false);
    }
  }, [initialLoad]);

  return (
    <AppDataContext.Provider value={{ iboNumber, isLoading }}>
      <div className={styles.wrapper}>
        <AppFrame
          mobileBridge={mobileBridge}
          navigation={[
            {
              title: t("my info"),
              icon: <MyInfoIcon />,
              path: Utils.PATHS.MY_INFO,
            },
            { title: "los", icon: <LOSIcon />, path: Utils.PATHS.LOS },
            {
              title: t("eagle tracker"),
              icon: <EagleTrackerIcon />,
              path: Utils.PATHS.EAGLE_TRACKER,
            },
            {
              title: t("reports"),
              icon: <ReportsIcon />,
              path: Utils.PATHS.REPORTS,
            },
            {
              title: t("settings"),
              icon: <SettingsIcon />,
              path: Utils.PATHS.SETTINGS,
            },
          ]}
          header={{
            title: "The Pulse",
            icon: (<IconDiamond size={42} />) as ReactNodeLike,
          }}
        >
          <main className={styles.main}>
            <Switch>
              <Route exact path={Utils.PATHS.MY_INFO}>
                <MyInfo />
              </Route>
              <Route exact path={Utils.PATHS.LOS}>
                <LOSPage />
              </Route>
              <Route exact path={Utils.PATHS.LOS_DETAILS_INFO}>
                <MyInfo />
              </Route>
              <Route exact path={Utils.PATHS.LOS_SEARCH}>
                <LOSSearchPage />
              </Route>
              <Route exact path={Utils.PATHS.LOS_TREE_VIEW}>
                <LOSTreeViewPage />
              </Route>
              <Route exact path={Utils.PATHS.LOS_BRANCH_VIEW}>
                <LOSBranchViewPage />
              </Route>
              <Route exact path={Utils.PATHS.REPORTS}>
                <Reports />
              </Route>
              <Route exact path={Utils.PATHS.REPORTS_INFO}>
                <MyInfo />
              </Route>
              <Route exact path={Utils.PATHS.SETTINGS}>
                <Settings />
              </Route>
              <Route exact path={Utils.PATHS.EAGLE_TRACKER}>
                <EagleTracker />
              </Route>
              <Route exact path={Utils.PATHS.EAGLE_TRACKER_INFO}>
                <EagleTrackerProfile />
              </Route>
              <Route path="*">
                <Redirect to={Utils.PATHS.MY_INFO} />
              </Route>
            </Switch>
          </main>
        </AppFrame>
      </div>
    </AppDataContext.Provider>
  );
}
